/**
 * useToolbarShortcuts Hook
 * Story 6.1: Implement Unified Toolbar Wrapper
 *
 * AC-6.1.4: Keyboard shortcuts are wired and functional
 * - Cmd/Ctrl+Z: Undo
 * - Cmd/Ctrl+Shift+Z: Redo
 * - Cmd/Ctrl+R: Refresh (CRITICAL: prevent browser refresh)
 * - Cmd/Ctrl+,: Settings
 * - Cmd/Ctrl+Enter: Present mode
 */
import { useEffect, useCallback } from 'react';
import { usePlatform } from './usePlatform';

interface ShortcutHandlers {
  onUndo?: () => void;
  onRedo?: () => void;
  onRefresh?: () => void;
  onSettings?: () => void;
  onPresent?: () => void;
}

/**
 * Hook to register global keyboard shortcuts for the module toolbar.
 *
 * @param handlers - Object with callback functions for each shortcut
 * @param enabled - Whether shortcuts should be active (default: true)
 *
 * @example
 * useToolbarShortcuts({
 *   onUndo: handleUndo,
 *   onRedo: handleRedo,
 *   onRefresh: () => queryClient.invalidateQueries(),
 *   onSettings: () => setShowSettings(true),
 *   onPresent: () => document.documentElement.requestFullscreen(),
 * });
 */
export function useToolbarShortcuts(
  handlers: ShortcutHandlers,
  enabled: boolean = true
) {
  const { modifierKey } = usePlatform();

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!enabled) return;

    // CRITICAL: Skip if event was already handled (e.g. by Grid)
    if (e.defaultPrevented) return;

    // CRITICAL: Skip if user is typing in input/textarea/contentEditable
    const target = e.target as HTMLElement;
    const tagName = target.tagName.toLowerCase();
    if (
      tagName === 'input' ||
      tagName === 'textarea' ||
      target.isContentEditable
    ) {
      return;
    }

    const modKey = e[modifierKey]; // e.metaKey (Mac) or e.ctrlKey (Win/Linux)

    // Cmd/Ctrl + Z: Undo
    if (modKey && e.key === 'z' && !e.shiftKey) {
      e.preventDefault();
      handlers.onUndo?.();
      return;
    }

    // Cmd/Ctrl + Shift + Z: Redo
    if (modKey && e.key === 'z' && e.shiftKey) {
      e.preventDefault();
      handlers.onRedo?.();
      return;
    }

    // Cmd/Ctrl + Y: Redo (Windows alternative)
    if (modKey && e.key === 'y' && !e.shiftKey) {
      e.preventDefault();
      handlers.onRedo?.();
      return;
    }

    // Cmd/Ctrl + R: Refresh (CRITICAL: prevent browser refresh)
    if (modKey && e.key === 'r' && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      handlers.onRefresh?.();
      return;
    }

    // Cmd/Ctrl + ,: Settings
    if (modKey && e.key === ',') {
      e.preventDefault();
      handlers.onSettings?.();
      return;
    }

    // Cmd/Ctrl + Enter: Present mode
    if (modKey && e.key === 'Enter') {
      e.preventDefault();
      handlers.onPresent?.();
      return;
    }
  }, [handlers, modifierKey, enabled]);

  useEffect(() => {
    // Use capture phase to intercept before browser defaults
    document.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => {
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [handleKeyDown]);
}
